import { useCallback, useEffect, useState } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Loader2, ShieldCheck, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { inr } from './IsaKpis';

const when = v => (v ? new Date(v).toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '');
const ALERT_WORD = { sync_error: 'Sync error', missing_snapshot: 'Missing snapshot', reconcile_mismatch: 'CRM changed after snapshot' };

const Section = ({ title, children, testid, className = '' }) => (
  <Card className={`p-4 ${className}`} data-testid={testid}>
    <h3 className="text-sm font-bold text-slate-900 mb-2">{title}</h3>
    {children}
  </Card>
);

export const IsaAuditTab = ({ month, missing, onChanged }) => {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState('');
  const load = useCallback(() => api.get('/inside-sales-audit/audit', { params: { month } }).then(r => setD(r.data)), [month]);
  useEffect(() => { load(); }, [load]);

  const act = async (key, fn, ok) => {
    setBusy(key);
    try { const r = await fn(); toast.success(ok(r.data)); await load(); onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed'); } finally { setBusy(''); }
  };
  const reconcile = () => act('recon', () => api.post('/inside-sales-audit/reconcile', { month }),
    r => `Checked ${r.checked} day(s) — ${r.mismatches} mismatch(es)`);
  const backfill = day => act(`bf-${day}`, () => api.post('/inside-sales-audit/snapshot', { date: day }),
    () => `${day} back-filled (Hot leads only)`);
  const ackAlert = id => act(`ack-${id}`, () => api.post(`/inside-sales-audit/alerts/${id}/ack`), () => 'Alert acknowledged');

  if (!d) return <div className="py-10 text-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin inline" /></div>;
  const open = d.alerts.filter(a => !a.acknowledged);
  return (
    <div className="grid lg:grid-cols-2 gap-4" data-testid="isa-audit-tab">
      <Section title={`Alerts (${open.length} open)`} testid="isa-alerts">
        {d.alerts.length === 0 && <p className="text-xs text-slate-400">No alerts.</p>}
        <div className="space-y-2 max-h-80 overflow-y-auto">
          {d.alerts.map(a => (
            <div key={a.id} className={`text-xs rounded-md border px-3 py-2 ${a.acknowledged ? 'border-slate-100 text-slate-400' : 'border-amber-200 bg-amber-50 text-amber-900'}`} data-testid={`isa-alert-${a.id}`}>
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                <div className="flex-1"><b>{ALERT_WORD[a.kind] || a.kind}</b> · {when(a.at)}<div>{a.message}</div>
                  {a.acknowledged && <div className="text-[10px]">Acknowledged by {a.acknowledged_by} {when(a.acknowledged_at)}</div>}</div>
                {!a.acknowledged && <Button size="sm" variant="outline" disabled={busy === `ack-${a.id}`} onClick={() => ackAlert(a.id)} className="h-6 text-[11px] bg-white" data-testid={`isa-alert-ack-${a.id}`}>Acknowledge</Button>}
              </div>
            </div>
          ))}
        </div>
      </Section>

      <Section title="Reconciliation" testid="isa-recon">
        <p className="text-xs text-slate-500 mb-2">Re-reads every saved day's Hot leads from Zoho CRM and records any difference. Snapshots are never changed. Latest check per day shown.</p>
        <Button size="sm" onClick={reconcile} disabled={busy === 'recon'} data-testid="isa-reconcile-btn">
          {busy === 'recon' ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <ShieldCheck className="w-3.5 h-3.5 mr-1.5" />}Reconcile {month}</Button>
        <div className="mt-3 space-y-1 max-h-56 overflow-y-auto text-xs">
          {d.reconciliations.filter((r, i, all) => all.findIndex(x => x.date === r.date) === i).map(r => (
            <div key={r.id} className={r.matches ? 'text-emerald-700' : 'text-amber-700'}>
              {r.matches ? <CheckCircle2 className="w-3 h-3 inline mr-1" /> : <AlertTriangle className="w-3 h-3 inline mr-1" />}
              {r.date}: {r.matches ? 'matches' : Object.entries(r.diff).map(([k, v]) => `${k} snapshot ${v.snapshot} → CRM ${v.crm}`).join('; ')}
              <span className="text-slate-400"> · {when(r.at)}</span>
            </div>
          ))}
        </div>
      </Section>

      <Section title={`Missing days (${missing.length})`} testid="isa-missing">
        <p className="text-xs text-slate-500 mb-2">Back-filling rebuilds Hot leads from the conversion date. Call Back and Unable to Reach for past days can't be rebuilt, so those penalties are not applied on back-filled days.</p>
        {missing.length === 0 && <p className="text-xs text-emerald-700">Every day so far has a snapshot.</p>}
        <div className="flex flex-wrap gap-2">
          {missing.map(m => (
            <Button key={m} size="sm" variant="outline" disabled={busy === `bf-${m}`} onClick={() => backfill(m)} className="h-7 text-[11px]" data-testid={`isa-backfill-${m}`}>
              {busy === `bf-${m}` && <Loader2 className="w-3 h-3 animate-spin mr-1" />}Back-fill {m}</Button>
          ))}
        </div>
      </Section>

      <Section title="Saved snapshots" testid="isa-snapshots">
        <div className="max-h-56 overflow-y-auto text-xs space-y-1">
          {d.snapshots.map(s => (
            <div key={s.date} className="flex gap-3">
              <span className="w-24 text-slate-800">{s.date}</span><span className="w-20 text-slate-500">{s.kind}</span>
              <span className="w-24 text-right">{inr(s.calc?.net)}</span>
              <span className="flex-1 text-slate-400 truncate">{when(s.captured_at)} · {s.captured_by} · <span className="font-mono">{s.hash.slice(0, 10)}</span></span>
            </div>
          ))}
        </div>
      </Section>

      <Section title="Calculation & activity log" testid="isa-logs" className="lg:col-span-2">
        <div className="max-h-72 overflow-y-auto text-xs space-y-2">
          {d.logs.map(l => (
            <div key={l.id} className="border-b border-slate-50 pb-1.5">
              <div className="text-slate-800">{l.detail}</div>
              {l.extra?.lines && <div className="text-[11px] text-slate-500">{l.extra.lines.join(' · ')}</div>}
              <div className="text-[10px] text-slate-400">{l.by} · {when(l.at)}</div>
            </div>
          ))}
        </div>
      </Section>

      <Section title="Scheduler runs (11:59 PM IST)" testid="isa-runs">
        {d.runs.length === 0 && <p className="text-xs text-slate-400">No scheduled run yet — the first one happens tonight at 11:59 PM IST.</p>}
        <div className="text-xs space-y-1">
          {d.runs.map(r => <div key={r._id} className={r.status === 'failed' ? 'text-rose-600' : 'text-slate-600'}>{when(r.at)} · {r.status}{r.detail ? ` — ${r.detail}` : ''}</div>)}
        </div>
      </Section>
    </div>
  );
};
